import React, { useState, useRef } from "react";
import {
  Button,
  VStack,
  Textarea,
  Text,
  useToast,
  Box,
  Input,
  FormControl,
  FormLabel,
  HStack,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import Dialog from "../../../components/Dialog";
import { WSMigration } from "../../../../services/remote/migration/WSMigration";

interface MigrationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  storeId: string;
}

export const MigrationDialog: React.FC<MigrationDialogProps> = ({
  isOpen,
  onClose,
  onSuccess,
  storeId,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [jsonContent, setJsonContent] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const migraionInstance = WSMigration.getInstance();

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        // Validate JSON
        JSON.parse(content);
        setJsonContent(JSON.stringify(JSON.parse(content), null, 2));
      } catch (error) {
        toast({
          title: t("error.invalidJson"),
          status: "error",
          duration: 3000,
        });
      }
    };
    reader.readAsText(file);
  };

  const handleSubmit = async () => {
    if (!jsonContent.trim()) {
      toast({
        title: t("error.jsonRequired"),
        status: "warning",
        duration: 3000,
      });
      return;
    }

    try {
      setIsLoading(true);
      const jsonData = JSON.parse(jsonContent);
      // Call your API endpoint here
      await migraionInstance.runMigration(jsonData, storeId);

      toast({
        title: t("migration.success"),
        status: "success",
        duration: 3000,
      });
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error("Migration error:", error);
      toast({
        title: t("error.migrationFailed"),
        description: error instanceof Error ? error.message : "Unknown error",
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      title={t("migration.importData")}
      description={""}
      showOkButton={false}
      showYesButton={false}
    >
      <VStack spacing={4} align="stretch">
        <FormControl>
          <FormLabel>{t("migration.uploadJsonFile")}</FormLabel>
          <Input
            type="file"
            accept=".json"
            onChange={handleFileUpload}
            ref={fileInputRef}
            p={1}
          />
        </FormControl>

        <Text textAlign="center" fontWeight="bold">
          {t("common.or")}
        </Text>

        <FormControl>
          <FormLabel>{t("migration.pasteJson")}</FormLabel>
          <Textarea
            value={jsonContent}
            onChange={(e) => setJsonContent(e.target.value)}
            placeholder={JSON.stringify({ example: "data" }, null, 2)}
            minH="200px"
            fontFamily="monospace"
            resize="vertical"
          />
        </FormControl>

        <HStack justify="flex-end" mt={4}>
          <Button onClick={onClose} variant="outline">
            {t("cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            isLoading={isLoading}
            isDisabled={!jsonContent.trim()}
          >
            {t("import")}
          </Button>
        </HStack>
      </VStack>
    </Dialog>
  );
};

export default MigrationDialog;
